import React from 'react';
import { Shield, FileText, Users, AlertCircle } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-pattern animate-gradient"></div>
      <div className="gradient-orb gradient-orb-1"></div>
      <div className="gradient-orb gradient-orb-2"></div>
      <div className="gradient-orb gradient-orb-3"></div>
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Terms of{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600">
              Service
            </span>
          </h1>
          <p className="text-xl text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
        
        <div className="space-y-8">
          <TermsSection
            icon={<Shield className="h-6 w-6 text-purple-400" />}
            title="1. Introduction"
            content="Welcome to Distrotopia LTD. By using our services, you agree to these terms. Please read them carefully. These terms govern your use of Distrotopia LTD's music distribution services and outline your rights and responsibilities."
          />

          <TermsSection
            icon={<FileText className="h-6 w-6 text-purple-400" />}
            title="2. Service Description"
            content="Distrotopia Ltd provides digital music distribution services to various streaming platforms and digital stores. We facilitate the delivery, sale, and streaming of your music content to these platforms on your behalf."
          />

          <TermsSection
            icon={<Users className="h-6 w-6 text-purple-400" />}
            title="3. Rights and Ownership"
            content="You retain 100% ownership of your music. By using our service, you grant us the right to distribute your music to our partner platforms. You warrant that you own or have the necessary rights to distribute the content you submit."
          />

          <TermsSection
            icon={<AlertCircle className="h-6 w-6 text-purple-400" />}
            title="4. Content Guidelines"
            content={
              <div className="space-y-4">
                <p>All submitted content must comply with our content guidelines. This includes but is not limited to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Original content or content you have rights to distribute</li>
                  <li>High-quality audio files meeting our technical specifications</li>
                  <li>Appropriate metadata and artwork</li>
                  <li>No copyright-infringing material</li>
                </ul>
              </div>
            }
          />

          <TermsSection
            icon={<FileText className="h-6 w-6 text-purple-400" />}
            title="5. Payment Terms"
            content="We collect and distribute revenue from streaming platforms according to our payment schedule. Payments are made when your balance reaches the minimum threshold. Detailed payment terms are available in your account dashboard."
          />

          <TermsSection
            icon={<AlertCircle className="h-6 w-6 text-purple-400" />}
            title="6. Termination"
            content="Either party may terminate this agreement with written notice. Upon termination, we will begin the process of removing your content from distribution platforms, which may take several weeks depending on the platform."
          />

          <TermsSection
            icon={<Shield className="h-6 w-6 text-purple-400" />}
            title="7. Updates to Terms"
            content="We may update these terms from time to time. We will notify you of any material changes. Your continued use of our services after such changes constitutes acceptance of the updated terms."
          />
        </div>

        <div className="mt-12 p-6 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <p className="text-center text-gray-400 text-sm">
            If you have any questions about these terms, please contact our support team at{' '}
            <a href="mailto:support@distrotopia.com" className="text-purple-400 hover:text-purple-300 transition-colors">
              support@distrotopia.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const TermsSection = ({ 
  icon, 
  title, 
  content 
}: { 
  icon: React.ReactNode, 
  title: string, 
  content: React.ReactNode 
}) => {
  return (
    <div className="bg-gray-900/50 rounded-lg p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all">
      <div className="flex items-center space-x-3 mb-4">
        <div className="bg-purple-500/10 p-2 rounded-lg">
          {icon}
        </div>
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      <div className="text-gray-300 leading-relaxed">
        {content}
      </div>
    </div>
  );
};

export default Terms;